import React, { useCallback, useMemo } from 'react'
import { Key } from 'react-aria'
import { ListState } from 'react-stately'
import { AnimatePresence } from 'framer-motion'
import invariant from 'tiny-invariant'
import * as R from 'remeda'
import { Flex } from '../../../core-components'
import { SearchInput } from '../../input'
import { DynamicCascadeItem, DynamicItem } from '../base-select/dynamicItemUtils'
import { SelectItemDataProvider } from '../base-select/SelectItemContext'
import { CascadeListAnimationWrapper } from '../base-select/BaseSelectCascade'
import { SelectListBase } from '../select-list/SelectListBase'
import { useSelectListKeyboardDelegate } from '../select-list/useSelectListKeyboardDelegate'
import {
  SelectEndContent,
  SelectItem,
  SelectSection,
  SelectStartContent,
} from '../select/PublicComponents'
import { useCascadeSelectState } from '../base-select/useCascaseSelectState'
import { CascadeItemSelectList } from '../base-select/CascadeItemSelectList'
import { MIN_SELECT_LIST_WIDTH } from '../base-select/constants'
import { ItemData } from '../base-select/itemData.types'
import { isCascadeSelectValues } from '../base-select/cascadeSelectUtils'
import { useCascadeListFocus } from '../base-select/CascadeListFocusContext'
import { CascadeSelectValue } from './PillSelect'

type Props<Value extends string | CascadeSelectValue> = {
  onClose: () => void
  selectedCascadeItem?: DynamicCascadeItem[]
  setSelectedCascadeItem?: React.Dispatch<React.SetStateAction<DynamicCascadeItem[] | undefined>>
  triggerWidth: number
  listState: ListState<DynamicItem>
  items: Record<Key, ItemData>
  values: readonly Value[]
  onChange: (values: Value[]) => void
} & (
  | {
      'aria-label': string
      'aria-labelledby'?: never
    }
  | {
      'aria-label'?: never
      'aria-labelledby': string
    }
) &
  (
    | {
        onSearchChange?: (value: string) => void
        search?: string
        hasUserProvidedSearch?: boolean
      }
    | { onSearchChange?: never; search?: never; hasUserProvidedSearch?: never }
  )
export const PillSelectList = <Value extends string | CascadeSelectValue>({
  values,
  onChange,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  onSearchChange,
  onClose,
  search = '',
  setSelectedCascadeItem,
  selectedCascadeItem,
  triggerWidth,
  listState,
  items,
  hasUserProvidedSearch,
}: Props<Value>) => {
  const onSelectCascadeItem = (item: DynamicCascadeItem) => {
    setSelectedCascadeItem?.([item])
    onSearchChange?.('')
  }

  const state = useCascadeSelectState(listState, onSelectCascadeItem)
  const onFocusSelectListItem = useCallback(
    (key: Key) => {
      state.selectionManager.setFocused(true)
      state.selectionManager.setFocusedKey(key)
    },
    [state]
  )

  const { queueFocus, ref: setListRef } = useCascadeListFocus()

  const searchPlaceholder = useMemo(() => {
    const defaultPlaceholder = 'Search...'
    if (selectedCascadeItem) {
      return selectedCascadeItem
        .map(({ label }) => label)
        .concat(defaultPlaceholder)
        .join(' > ')
    }
    return defaultPlaceholder
  }, [selectedCascadeItem])

  const keyboardDelegate = useSelectListKeyboardDelegate(state.collection, state.disabledKeys)
  return (
    <Flex
      flexDirection='column'
      borderRadius={8}
      boxShadow='border/popover'
      overflow='hidden'
      maxHeight='inherit'
      minWidth={Math.max(triggerWidth, MIN_SELECT_LIST_WIDTH)}
      bg='bg/dropdown'
    >
      {onSearchChange ? (
        <SearchInput
          variant='nested'
          aria-label='Filter selection options'
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
          value={search}
        />
      ) : null}
      <Flex flexDirection='row' overflow='hidden' maxHeight='inherit'>
        <SelectItemDataProvider value={items}>
          <SelectListBase
            keyboardDelegate={keyboardDelegate}
            state={state}
            onClose={onClose}
            onFocusItem={onFocusSelectListItem}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            variant='nested'
            search={selectedCascadeItem?.length || hasUserProvidedSearch ? undefined : search}
          />
          <AnimatePresence initial={false}>
            {setSelectedCascadeItem &&
              selectedCascadeItem?.map((item, level, list) => {
                invariant(
                  isCascadeSelectValues(values),
                  'values must be an array of CascadeSelectValue'
                )
                const { currentPath, prevPath } = getItemPaths(list, level)
                const cascadeValues = R.unique(
                  (values as readonly CascadeSelectValue[])
                    .filter(value => value.path.join('.').startsWith(currentPath))
                    ?.map(({ value, path }) => (path.length > level + 1 ? path[level + 1] : value))
                )

                return (
                  <CascadeListAnimationWrapper<string[]>
                    key={`${item.value}-${level}`}
                    itemValue={item.value}
                    value={cascadeValues}
                  >
                    <CascadeItemSelectList
                      value={cascadeValues}
                      onClose={R.doNothing}
                      width={Math.max(triggerWidth, MIN_SELECT_LIST_WIDTH)}
                      onChange={(value: string | string[]) => {
                        const selectedValues = values as readonly CascadeSelectValue[]

                        const path =
                          selectedCascadeItem?.slice(0, level + 1).map(item => item.value) ?? []
                        const valuesArray = (Array.isArray(value) ? value : [value]).filter(
                          value =>
                            !selectedValues.some(currentValue =>
                              currentValue.path.join('.').startsWith([...path, value].join('.'))
                            )
                        )
                        const newValues = valuesArray.map(value => ({
                          value,
                          path,
                        }))

                        onChange([
                          ...selectedValues.filter(
                            value => value.path.join('.') !== path.join('.')
                          ),
                          ...newValues,
                        ] as Value[])
                      }}
                      onSelectCascadeItem={cascadeItem => {
                        setSelectedCascadeItem((current = []) => [
                          ...current.slice(0, level + 1),
                          cascadeItem,
                        ])
                        queueFocus(`${currentPath}.${cascadeItem.value}`)
                        onSearchChange?.('')
                      }}
                      onPrevList={() => queueFocus(prevPath, list[level].value)}
                      onFocusItem={key => queueFocus(currentPath, key)}
                      onNextList={() => {
                        if (list.length > level + 1) {
                          queueFocus(`${currentPath}.${list[level + 1].value}`)
                        }
                      }}
                      path={currentPath}
                      eventsRef={setListRef}
                      search={
                        // search only the last cascade panel
                        level === list.length - 1 && !hasUserProvidedSearch ? search : undefined
                      }
                    >
                      {item.children}
                    </CascadeItemSelectList>
                  </CascadeListAnimationWrapper>
                )
              })}
          </AnimatePresence>
        </SelectItemDataProvider>
      </Flex>
    </Flex>
  )
}

PillSelectList.Item = SelectItem
PillSelectList.StartContent = SelectStartContent
PillSelectList.EndContent = SelectEndContent
PillSelectList.Section = SelectSection

// The same as in BaseSelect
const getItemPaths = (list: readonly { readonly value: string }[], level: number) => {
  const prevPath = list
    .slice(0, level)
    .map(item => item.value)
    .join('.')
  const currentPath = list
    .slice(0, level + 1)
    .map(item => item.value)
    .join('.')
  return { currentPath, prevPath }
}
